/**
 * Нормализация телефона в E.164 — ЕДИНСТВЕННОЕ место правила (§1.2, §6.8).
 *
 * Живёт в @cc/shared, потому что правило нужно всем четырём слоям: приёму
 * (@cc/domain/pure/phone.ts переэкспортирует отсюда), логам (redact.ts),
 * браузеру (apps/web не зависит от @cc/domain). Вторая реализация здесь уже
 * была и расходилась с доменной: она возвращала '79991234567' без '+', то есть
 * значение, которое не проходит CHECK на contacts.phone_e164, и отбрасывала
 * городские номера из 10 цифр, которые домен принимал.
 *
 * Номера приходят из разных кабинетов в разном виде: +7, 8, со скобками,
 * с пробелами, иногда с 10 цифрами без кода страны. Если не привести их
 * к одному виду на входе, дедуп пропустит повтор — то есть мы второй раз
 * заплатим за уже обзвонённый номер, и звонок уйдёт не туда.
 *
 * Чистые функции: ни IO, ни времени.
 */
/** Совпадает с CHECK-констрейнтом contacts.phone_e164 / contact_phones.phone_e164 (§5.3). */
const E164_RE = /^\+[1-9][0-9]{7,14}$/;
const RU_DIGITS = 11;
/**
 * Российский номер → '+7XXXXXXXXXX'. Не похоже на номер — null, а не исключение:
 * на приёме это штатная ситуация, строка помечается decision='invalid_phone'.
 */
export function normalizeRuPhone(input) {
    if (input === null || input === undefined)
        return null;
    const digits = input.replace(/\D/g, '');
    if (digits.length === 0)
        return null;
    let national = digits;
    // 8 999 123-45-67 → 79991234567
    if (national.length === RU_DIGITS && national.startsWith('8')) {
        national = `7${national.slice(1)}`;
    }
    // 9991234567 / 4951234567 без кода страны.
    // Первая цифра 7 или 8 здесь означает обрезанный номер (кодов регионов и
    // операторов РФ на 7 и 8 нет), достраивать его нельзя — уедет звонок не туда.
    if (national.length === 10 && !national.startsWith('7') && !national.startsWith('8')) {
        national = `7${national}`;
    }
    if (national.length !== RU_DIGITS || !national.startsWith('7'))
        return null;
    const e164 = `+${national}`;
    return E164_RE.test(e164) ? e164 : null;
}
/** Проверка формата, совпадающая с CHECK-констрейнтом в БД. */
export function isE164(value) {
    return E164_RE.test(value);
}
/**
 * Можно ли набирать этот номер со станции: только российский и только в том
 * виде, в каком его принимает диалплан (`_+7XXXXXXXXXX`, §9.5).
 *
 * Отдельная функция, потому что правило «международку не набираем» проверяется
 * трижды и в трёх разных процессах: диалпланом, доменом при выдаче намерения и
 * телефонией на StasisStart. Нормализация при этом остаётся одна — эта же.
 * Сравнение с самим собой, а не `startsWith('+7')`: '+7 (999) 123-45-67' —
 * российский номер, но набирать станции нужно нормализованный.
 */
export function isRuDialable(value) {
    return normalizeRuPhone(value) === value;
}
/**
 * Маскирование для логов: полный номер живёт только в БД (§9.4).
 * Вход — E.164; всё остальное превращается в '***' целиком, потому что
 * маскировать по позициям строку неизвестной формы нельзя.
 */
export function maskPhone(value) {
    if (!isE164(value))
        return '***';
    return `${value.slice(0, 5)}***${value.slice(-4)}`;
}
/** Для показа диспетчеру: +7 (999) 123-45-67 */
export function formatPhone(phone) {
    const normalized = normalizeRuPhone(phone);
    if (!normalized)
        return phone;
    const p = normalized.slice(1); // без '+'
    return `+${p.slice(0, 1)} (${p.slice(1, 4)}) ${p.slice(4, 7)}-${p.slice(7, 9)}-${p.slice(9, 11)}`;
}
/**
 * Маска для экрана: +7 (999) ***-**-67. Отличается от `maskPhone` намеренно —
 * в логе номер читает человек с грепом, в списке на экране диспетчер сверяет
 * его глазами с карточкой, и разделители там нужны.
 */
export function maskPhoneDisplay(phone) {
    const normalized = normalizeRuPhone(phone);
    if (!normalized)
        return '***';
    const p = normalized.slice(1);
    return `+${p.slice(0, 1)} (${p.slice(1, 4)}) ***-**-${p.slice(9, 11)}`;
}
//# sourceMappingURL=phone.js.map